import moment from 'moment'
import { IAreaProps } from './Area'
import { IDivisionProps } from './Division'
import { IFileProps } from './File'
import AgencyEntity, { IAgencyProps } from './Agency'
import PriceEntity, { IPriceProps } from './Price'
import { HospitalRocotime, HospitalAgency, HospitalProperties, HospitalFlags } from '@/infrastructure/network/Monitaro/schema/models'

export interface IHospitalSubmissionDocProps {
  id?: string | null
  chargeId?: string | null
  mediaId?: string | null
  file?: IFileProps | null
  editable: boolean
  submittedAt: string | null
}

export interface IHospitalProps {
  id?: string | null
  priceId: string | null
  data?: File | null
  keeps?: IFileProps[] | null
  submissionDoc?: IHospitalSubmissionDocProps | null
  agencies?: IAgencyProps[] | null
  divisions?: IDivisionProps[] | null
  areas: IAreaProps[] | null
  name: string | null
  kana: string | null
  term: number | null
  type: string | null
  status: string | null
  chargeable: boolean | null
  approved: boolean | null
  activation: boolean | null
  doctorsCount?: number
  usersCount?: number
  price?: IPriceProps | null
  rocotime: HospitalRocotime | null
  agency: HospitalAgency | null
  properties: HospitalProperties | null
  flags: HospitalFlags | null
  joinedAt: string | null
  startedAt: string | null
  createdAt: string | null
  updatedAt: string | null
  leavedAt?: string | null
  paymentStatus?: string | null
}

export default class HospitalEntity {
  private _props: IHospitalProps

  constructor(props?: IHospitalProps | null) {
    this._props = EmptyHospitalPropsFactory(props)
  }

  get props(): IHospitalProps {
    return this._props
  }

  get clone(): IHospitalProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined | null {
    return this._props.id
  }

  get priceId(): string | null {
    return this._props.priceId
  }

  get agencies(): AgencyEntity[] | null | undefined {
    return this._props.agencies ? this._props.agencies.map((prop) => new AgencyEntity(prop)) : []
  }

  get name(): string | null {
    return this._props.name
  }

  get kana(): string | null {
    return this._props.kana
  }

  get term(): number | null {
    return this._props.term
  }

  get type(): string | null {
    return this._props.type
  }

  get status(): string {
    // 承認制がオンの場合はステータスを表示
    return status.find((prop) => prop.value === this._props.status)?.text || '-'
  }

  get doctorsCount(): number | undefined {
    return this._props.doctorsCount
  }

  get usersCount(): number | undefined {
    return this._props.usersCount
  }

  get joinedAt(): string | null {
    return moment(this._props.joinedAt).format('YYYY/MM/DD')
  }

  get startedAt(): string | null {
    return this._props.startedAt
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get price(): PriceEntity | null | undefined {
    return new PriceEntity(this._props.price)
  }

  get rocotime(): HospitalRocotime | null | undefined {
    return this._props.rocotime
  }

  get agency(): HospitalAgency | null | undefined {
    return this._props.agency
  }

  get properties(): HospitalProperties | null | undefined {
    return this._props.properties
  }

  get flags(): HospitalFlags | null | undefined {
    return this._props.flags
  }

  /** 課金対象（課金ON）のエリアのみ。API の Charge::getChargeFee と同じ判定基準です */
  private get chargeableAreas(): IAreaProps[] {
    return this._props.areas?.filter((area) => area.chargeable) || []
  }

  /** 課金対象エリアが Lite のみ（= lite_fee で課金される）か */
  get lite(): boolean {
    const areas = this.chargeableAreas
    return areas.length > 0 && areas.every((area) => area.type === 'lite')
  }

  get chargeable(): string {
    if (this._props.chargeable) {
      return this.lite ? 'Lite' : moment().isSameOrAfter(this.startedAt) ? '有料' : moment(this.startedAt).format('YYYY/MM/DD 有料化予定')
    }
    return '未定'
  }

  get charged(): boolean {
    return !!this._props.chargeable && !this.lite
  }

  get areas(): IAreaProps[] | null {
    return this._props.areas
  }

  get hasLeft(): boolean {
    return !!this._props.leavedAt
  }

  get paymentStatus(): string {
    if (!this.hasLeft) {
      return '未定'
    }
    if (this._props.paymentStatus === 'paid') {
      return '最終支払い完了'
    }
    if (this._props.paymentStatus === 'unpaid') {
      return '次回最終支払い'
    }
    return '未定'
  }

  get paymentStatusColor(): string | undefined {
    if (this._props.paymentStatus === 'paid') {
      return 'secondary'
    }
    if (this._props.paymentStatus === 'unpaid') {
      return 'alert'
    }
    return undefined
  }
}

export const status = [
  { text: '未承認', value: 'review' },
  { text: '承認済', value: 'approved' },
  { text: '不許可', value: 'reject' }
]

// 有料MR用ヘッダー（すべてのカラムを含む）
export const headers = [
  { text: '病院名', value: 'name' },
  { text: '', value: 'selectable' },
  { text: '担当日', value: 'joinedAt' },
  { text: '最終支払い', value: 'paymentStatus' },
  { text: '', value: 'chargeable' },
  { text: '', value: 'status' }
]

// 検索用のNGワード
export const ng = ['病院', '医療', 'センター']

export const EmptyHospitalPropsFactory = (props?: Partial<IHospitalProps> | null): IHospitalProps => ({
  id: null,
  data: null,
  keeps: null,
  submissionDoc: null,
  areas: [],
  divisions: [],
  priceId: null,
  name: null,
  kana: null,
  chargeable: null,
  approved: null,
  activation: null,
  term: null,
  type: null,
  status: null,
  joinedAt: null,
  startedAt: null,
  createdAt: null,
  updatedAt: null,
  leavedAt: null,
  paymentStatus: null,
  ...props,
  rocotime: EmptyHospitalRocotimeFactory(props?.rocotime),
  agency: EmptyHospitalAgencyFactory(props?.agency),
  properties: EmptyHospitalPropertiesFactory(props?.properties),
  flags: EmptyHospitalFlagsFactory(props?.flags)
})

export const EmptyHospitalRocotimeFactory = (props?: Partial<HospitalRocotime> | null): HospitalRocotime => ({
  use: false,
  changed: null,
  ...props
})

export const EmptyHospitalAgencyFactory = (props?: Partial<HospitalAgency> | null): HospitalAgency => ({
  supervisor: null,
  created: null,
  ...props
})

export const EmptyHospitalPropertiesFactory = (props?: Partial<HospitalProperties> | null): HospitalProperties => ({
  activation: false,
  major: null,
  code: null,
  sub_domain: null,
  prefecture: null,
  beds: null,
  notes: null,
  ...props
})

export const EmptyHospitalFlagsFactory = (props?: Partial<HospitalFlags> | null): HospitalFlags => ({
  approved: false,
  maintenance: false,
  thermometer: false,
  team: false,
  chatgpt: false,
  schedule: false,
  result: false,
  ...props
})
